import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewEncapsulation,
} from "@angular/core";
import { Subject, Subscription, of } from "rxjs";
import { debounceTime, switchMap } from "rxjs/operators";

import { LocalizeService, AuthService } from "../../insa-services";
import { appStateProvider } from "../../insa-providers";
import { appConfig } from "../../../insaweb.config";

import {
  ControlTableService,
  TraderSecurityDtoModel,
} from "./control-table.service";
import {
  ControlTable,
  ControlTableRow,
  ControlTableSecurity,
  createEmptyRow,
} from "./control-table.models";

@Component({
  selector: "app-control-table-main",
  templateUrl: "./control-table.component.html",
  styles: [require("./control-table.component.css").toString()],
  encapsulation: ViewEncapsulation.None,
})
export class ControlTableComponent implements OnInit, OnDestroy {
  tables: ControlTable[] = [];
  selectedTableId: string | null = null;

  searchQuery = "";
  searchResults: TraderSecurityDtoModel[] = [];
  showSearchResults = false;

  liquidityCurrency = "";

  orderModalOpen = false;
  orderModalSecurity: ControlTableSecurity | null = null;
  orderModalSide: "Buy" | "Sell" = "Buy";
  orderModalPickMode = false;

  depthModalOpen = false;
  depthModalSecurity: ControlTableSecurity | null = null;

  private searchSubject = new Subject<string>();
  private searchSub: Subscription;
  private tablesSub: Subscription;
  private pollInterval: any;

  constructor(
    private _localize: LocalizeService,
    private _element: ElementRef,
    private _authService: AuthService,
    private _appStateProvider: appStateProvider,
    private _service: ControlTableService,
  ) {}

  ngOnInit(): void {
    const defaultCurrency = appConfig.initialSettings?.currency ?? "";
    this.liquidityCurrency =
      this._appStateProvider.selectedCurrency?.text || defaultCurrency;

    this.tablesSub = this._service.tables.subscribe((tables) => {
      this.tables = tables;

      if (
        !this.selectedTableId ||
        !tables.some((t) => t.id === this.selectedTableId)
      ) {
        this.selectedTableId = tables.length > 0 ? tables[0].id : null;
      }
      this.reapplyLocalize();
    });

    this.searchSub = this.searchSubject
      .pipe(
        debounceTime(300),
        switchMap((q) => {
          if (!q || q.trim().length < 3) return of([]);
          return this._service.searchSecurities(
            q.trim(),
            this.liquidityCurrency,
          );
        }),
      )
      .subscribe((results) => {
        this.searchResults = results ?? [];
        this.showSearchResults = true;
      });

    this.pollInterval = setInterval(() => this.refreshRows(), 5000);
  }

  ngOnDestroy(): void {
    if (this.searchSub) this.searchSub.unsubscribe();
    if (this.tablesSub) this.tablesSub.unsubscribe();
    if (this.pollInterval) clearInterval(this.pollInterval);
  }

  get selectedTable(): ControlTable | null {
    return this.tables.find((t) => t.id === this.selectedTableId) ?? null;
  }

  get rows(): ControlTableRow[] {
    return this.selectedTable ? this.selectedTable.rows : [];
  }

  onSelectTable(id: string): void {
    this.selectedTableId = id;
    this.refreshRows();
  }

  onSearch(): void {
    this.searchSubject.next(this.searchQuery);
  }

  hideSearchResults(): void {
    setTimeout(() => {
      this.showSearchResults = false;
    }, 200);
  }

  addSecurity(sec: TraderSecurityDtoModel): void {
    if (!this.selectedTableId) return;
    const security = this.toSecurity(sec);
    const row = createEmptyRow(security);
    this._service.addSecurity(this.selectedTableId, row);
    this.searchQuery = "";
    this.searchResults = [];
    this.showSearchResults = false;
    this.refreshRow(row);
  }

  removeSecurity(row: ControlTableRow): void {
    if (!this.selectedTableId) return;
    this._service.removeSecurity(this.selectedTableId, row.security.SecIndex);
  }

  isAlreadyAdded(sec: TraderSecurityDtoModel): boolean {
    return this.rows.some((r) => r.security.SecIndex === sec.SecIndex);
  }

  private refreshRows(): void {
    for (const row of this.rows) {
      this.refreshRow(row);
    }
  }

  private refreshRow(row: ControlTableRow): void {
    const sec = row.security;

    this._service.realTimeL1(sec).subscribe((data) => {
      row.loading = false;
      if (data && data.length > 0) {
        const l1 = data[0];
        const hasMid =
          row.isDomestic &&
          l1.bid != null &&
          l1.bid > 0 &&
          l1.ask != null &&
          l1.ask > 0;
        row.price =
          hasMid && l1.bid != null && l1.ask != null
            ? (l1.bid + l1.ask) / 2
            : l1.price;
        row.bestBid = l1.bid != null && l1.bid > 0 ? l1.bid : null;
        row.bestBidQty = row.bestBid != null ? (l1.bidQuantity ?? 0) : null;
        row.bestAsk = l1.ask != null && l1.ask > 0 ? l1.ask : null;
        row.bestAskQty = row.bestAsk != null ? (l1.askQuantity ?? 0) : null;
      }
    });

    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - 10);
    this._service.eodPricesHistory(sec, fromDate, toDate).subscribe((bars) => {
      if (bars && bars.length > 0) {
        const last = bars[bars.length - 1];
        row.open = last.open;
        row.high = last.high;
        row.low = last.low;
        row.volume = last.volume;
      }
    });
  }

  openOrder(row: ControlTableRow, side: "Buy" | "Sell"): void {
    this.orderModalSecurity = row.security;
    this.orderModalSide = side;
    this.orderModalPickMode = false;
    this.orderModalOpen = true;
    this.reapplyLocalize();
  }

  openCreateOrder(): void {
    this.orderModalSecurity = null;
    this.orderModalSide = "Buy";
    this.orderModalPickMode = true;
    this.orderModalOpen = true;
    this.reapplyLocalize();
  }

  closeOrderModal(): void {
    this.orderModalOpen = false;
    this.orderModalSecurity = null;
    this.orderModalPickMode = false;
  }

  onOrderModalRemoveSecurity(secIndex: number): void {
    if (this.selectedTableId) {
      this._service.removeSecurity(this.selectedTableId, secIndex);
    }
    this.closeOrderModal();
  }

  openDepth(row: ControlTableRow): void {
    if (!row.isDomestic) return;
    this.depthModalSecurity = row.security;
    this.depthModalOpen = true;
    this.reapplyLocalize();
  }

  closeDepthModal(): void {
    this.depthModalOpen = false;
    this.depthModalSecurity = null;
  }

  private toSecurity(sec: TraderSecurityDtoModel): ControlTableSecurity {
    return {
      SecIndex: sec.SecIndex,
      SecNumber: sec.SecNumber,
      SecISIN: sec.SecISIN,
      SecStockExchange: sec.SecStockExchange,
      SecSymbol: sec.SecSymbol,
      SecName: sec.SecName,
      SecCurrSymbol: sec.SecCurrSymbol,
      FxRate: sec.FxRate,
      StockExchangeCode: sec.StockExchangeCode,
      StockExchangeName: sec.StockExchangeName,
      EODHDStockExchangeCode: sec.EODHDStockExchangeCode,
      SecType: sec.SecType,
      SecCurr: sec.SecCurr,
      SecFactor: sec.SecFactor,
      SecContractSize: sec.SecContractSize,
    };
  }

  formatN(value: number | null | undefined, decimals = 2): string {
    if (value === null || value === undefined || isNaN(value as number))
      return "";
    return (kendo as any).toString(value, "n" + decimals);
  }

  symbolOf(row: ControlTableRow): string {
    return (
      row.security.SecSymbol?.trim() || row.security.SecNumber?.trim() || ""
    );
  }

  private reapplyLocalize(): void {
    setTimeout(
      () => this._localize.applyTranslations(this._element.nativeElement),
      0,
    );
  }
}



// WEBPACK FOOTER //
// ./app/insa-components/insa-control-table/control-table.component.ts